// pages/components/settings/ResumeManagement.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Loader2, Save, X } from 'lucide-react';
import apiRequest from '@/services/requests';

const ResumeManagement = () => {
const [loading, setLoading] = useState(true);
const [activeSection, setActiveSection] = useState('skills');
const [resumeData, setResumeData] = useState(null);
const [editingItem, setEditingItem] = useState(null);
const [addingNew, setAddingNew] = useState(false);

useEffect(() => {
loadResumeData();
}, []);

const loadResumeData = async () => {
setLoading(true);
try {
const res = await apiRequest.getResumeData();
if (res.success) {
setResumeData(res.data);
}
} catch (error) {
console.error('Error loading resume data:', error);
}
setLoading(false);
};

const sections = [
{ id: 'skills', label: 'Skills', key: 'Skills' },
{ id: 'experience', label: 'Experience', key: 'Experiences' },
{ id: 'education', label: 'Education', key: 'Educations' },
{ id: 'certifications', label: 'Certifications', key: 'Certifications' },
{ id: 'projects', label: 'Projects', key: 'Projects' }
];

const handleDelete = async (section, itemId) => {
if (!confirm('Are you sure you want to delete this item?')) return;

try {
const deleteMap = {
skills: apiRequest.deleteSkill,
experience: apiRequest.deleteExperience,
education: apiRequest.deleteEducation,
certifications: apiRequest.deleteCertification,
projects: apiRequest.deleteProject
};

await deleteMap[section](itemId);
loadResumeData();
} catch (error) {
console.error('Error deleting:', error);
alert('Failed to delete item');
}
};

const renderSkillForm = (skill = {}) => (
<div className="space-y-4">
<input
type="text"
placeholder="Skill Name"
defaultValue={skill.skill_name || ''}
className="w-full px-4 py-2 rounded-lg border border-slate-300"
id="skill_name"
/>
<select
defaultValue={skill.proficiency_level || 'intermediate'}
className="w-full px-4 py-2 rounded-lg border border-slate-300"
id="proficiency_level"
>
<option value="beginner">Beginner</option>
<option value="intermediate">Intermediate</option>
<option value="advanced">Advanced</option>
<option value="expert">Expert</option>
</select>
</div>
);

const renderExperienceForm = (exp = {}) => (
<div className="space-y-4">
<input type="text" placeholder="Job Title" defaultValue={exp.job_title || ''} 
className="w-full px-4 py-2 rounded-lg border border-slate-300" id="job_title" />
<input type="text" placeholder="Company" defaultValue={exp.company || ''} 
className="w-full px-4 py-2 rounded-lg border border-slate-300" id="company" />
<div className="grid grid-cols-2 gap-4">
<input type="date" defaultValue={exp.start_date || ''} 
className="w-full px-4 py-2 rounded-lg border border-slate-300" id="start_date" />
<input type="date" defaultValue={exp.end_date || ''} 
className="w-full px-4 py-2 rounded-lg border border-slate-300" id="end_date" />
</div>
<textarea placeholder="Description" defaultValue={exp.description || ''} rows={3}
className="w-full px-4 py-2 rounded-lg border border-slate-300" id="description" />
</div>
);

const renderEducationForm = (edu = {}) => (
<div className="space-y-4">
<input type="text" placeholder="Degree" defaultValue={edu.degree || ''} 
className="w-full px-4 py-2 rounded-lg border border-slate-300" id="degree" />
<input type="text" placeholder="Institution" defaultValue={edu.institution || ''} 
className="w-full px-4 py-2 rounded-lg border border-slate-300" id="institution" />
<div className="grid grid-cols-2 gap-4">
<input type="date" defaultValue={edu.start_date || ''} 
className="w-full px-4 py-2 rounded-lg border border-slate-300" id="start_date" />
<input type="date" defaultValue={edu.end_date || ''} 
className="w-full px-4 py-2 rounded-lg border border-slate-300" id="end_date" />
</div>
</div>
);

const handleSave = async () => {
try {
const formData = {};
const form = document.querySelector('.edit-form');
form.querySelectorAll('input, select, textarea').forEach(el => {
formData[el.id] = el.value;
});

if (editingItem) {
const updateMap = {
skills: apiRequest.updateSkill,
experience: apiRequest.updateExperience,
education: apiRequest.updateEducation,
certifications: apiRequest.updateCertification,
projects: apiRequest.updateProject
};
await updateMap[activeSection](editingItem.id, formData);
} else {
const addMap = {
skills: apiRequest.addSkill,
experience: apiRequest.addExperience,
education: apiRequest.addEducation,
certifications: apiRequest.addCertification,
projects: apiRequest.addProject
};
await addMap[activeSection](formData);
}

setEditingItem(null);
setAddingNew(false);
loadResumeData();
} catch (error) {
console.error('Error saving:', error);
alert('Failed to save changes');
}
};

if (loading) {
return (
<div className="flex items-center justify-center py-12">
<Loader2 className="animate-spin text-indigo-600" size={32} />
</div>
);
}

if (!resumeData) {
return (
<div className="text-center py-12">
<p className="text-slate-600 mb-4">No resume data found. Please upload a resume first.</p>
</div>
);
}

const currentSection = sections.find(s => s.id === activeSection);
const items = resumeData[currentSection.key] || [];

return (
<div className="space-y-6">
<h2 className="hidden text-2xl font-bold text-slate-900">Resume Management</h2>

{/* Section Tabs */}
<div className="flex gap-2 flex-wrap">
{sections.map(section => (
<button
key={section.id}
onClick={() => {
setActiveSection(section.id);
setEditingItem(null);
setAddingNew(false);
}}
className={`px-4 py-2 rounded-lg font-medium transition-all ${
activeSection === section.id
? 'bg-indigo-600 text-white shadow-md'
: 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
}`}
>
{section.label}
</button>
))}
</div>

{/* Add New Button */}
{!addingNew && !editingItem && (
<button
onClick={() => setAddingNew(true)}
className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all"
>
<Plus size={20} />
Add {currentSection.label.slice(0, -1)}
</button>
)}

{/* Add/Edit Form */}
{(addingNew || editingItem) && (
<div className="bg-slate-50 p-6 rounded-xl border-2 border-indigo-200 edit-form">
<h3 className="font-bold text-slate-900 mb-4">
{editingItem ? 'Edit' : 'Add New'} {currentSection.label.slice(0, -1)}
</h3>

{activeSection === 'skills' && renderSkillForm(editingItem)}
{activeSection === 'experience' && renderExperienceForm(editingItem)}
{activeSection === 'education' && renderEducationForm(editingItem)}

<div className="flex gap-3 mt-4">
<button
onClick={handleSave}
className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg"
>
<Save size={18} />
Save
</button>
<button
onClick={() => {
setEditingItem(null);
setAddingNew(false);
}}
className="flex items-center gap-2 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-lg"
>
<X size={18} />
Cancel
</button>
</div>
</div>
)}

{/* Items List */}
<div className="space-y-3">
{items.length === 0 ? (
<p className="text-slate-500 text-center py-8">No {currentSection.label.toLowerCase()} added yet</p>
) : (
items.map(item => (
<div key={item.id} className="bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-all">
<div className="flex items-start justify-between">
<div className="flex-1">
{activeSection === 'skills' && (
<div>
<p className="font-semibold text-slate-900">{item.skill_name}</p>
<p className="text-sm text-slate-600">{item.proficiency_level}</p>
</div>
)}
{activeSection === 'experience' && (
<div>
<p className="font-semibold text-slate-900">{item.job_title}</p>
<p className="text-sm text-slate-600">{item.company}</p>
<p className="text-xs text-slate-500 mt-1">{item.start_date} - {item.end_date || 'Present'}</p>
</div>
)}
{activeSection === 'education' && (
<div>
<p className="font-semibold text-slate-900">{item.degree}</p>
<p className="text-sm text-slate-600">{item.institution}</p>
<p className="text-xs text-slate-500 mt-1">{item.start_date} - {item.end_date || 'Present'}</p>
</div>
)}
</div>
<div className="flex gap-2">
<button
onClick={() => setEditingItem(item)}
className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
>
<Edit2 size={18} />
</button>
<button
onClick={() => handleDelete(activeSection, item.id)}
className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
>
<Trash2 size={18} />
</button>
</div>
</div>
</div>
))
)}
</div>
</div>
);
};

export default ResumeManagement;